import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { FaFileInvoice, FaDownload, FaCalendarAlt, FaFileAlt } from "react-icons/fa";
import { useBuilding } from "../context/BuildingContext";
import { formatCurrencyUY, formatDateLocalized } from "../utils/formatters";
import { tolerantId } from "../utils/firestoreHelpers";
import { consultaDelCierre, leerGastosPorId } from "../utils/consultasEdificio";
import { isCierreApproved, isExpenseApartmentPublishedToResidents } from "../utils/cierrePublicacion";
import { occupancyCoversPeriod } from "../utils/occupancies";
import DataTable from "../components/DataTable";
import EmptyState from "../components/EmptyState";
import {
  cardBase,
  inputBase,
  colors,
  spacing,
  typography,
  badgeBase,
} from "../utils/designTokens";
import { useIsMobile } from "../hooks/useResponsive";
import { AppScreen, AppHeader, AppCard, AppEmptyState, SectionLabel } from "../components/app";
import { appColors, appType, appSpacing, appShadows } from "../utils/appTheme";
import IconBox from "../components/app/IconBox";

// Helper para parsear invoiceDate (Firestore Timestamp o string) a Date
const parseInvoiceDate = (val) => {
  if (!val) return null;
  if (val.seconds) return new Date(val.seconds * 1000);
  if (val.toDate) return val.toDate();
  if (val instanceof Date) return val;
  return new Date(val);
};

// Botón del stepper ‹ › del selector de cierre (mobile)
const stepBtn = (disabled) => ({
  width: 34,
  height: 34,
  borderRadius: 999,
  border: "none",
  background: disabled ? "transparent" : appColors.surface,
  color: disabled ? appColors.ink3 : appColors.blue,
  fontSize: 20,
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.4 : 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const Documentos = ({ setSelected, goBack }) => {
  const { t } = useTranslation("documentos");
  const monthNames = t("months", { returnObjects: true });
  const { selectedBuilding, selectedUnit } = useBuilding();
  const isMobile = useIsMobile();

  const [cierres, setCierres] = useState([]);
  const [selectedCierreId, setSelectedCierreId] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [suppliersMap, setSuppliersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingExpenses, setLoadingExpenses] = useState(false);

  // Fetch cierres aprobados y mapa de proveedores
  useEffect(() => {
    const fetchCierresAndSuppliers = async () => {
      if (!selectedBuilding?.buildingid) return;
      setLoading(true);

      try {
        // Fetch cierres del edificio
        const cierresQ = query(
          collection(db, "montlyaccount"),
          where("buildingid", "in", tolerantId(selectedBuilding.buildingid))
        );
        const cierresSnap = await getDocs(cierresQ);
        let allCierres = cierresSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.state === "ok" && isCierreApproved(c));

        // Residente saliente (unidad histórica de solo-lectura): limitar los
        // documentos a los meses que efectivamente ocupó la unidad.
        if (
          selectedUnit?.isHistorical &&
          Array.isArray(selectedUnit.occupancyPeriods) &&
          selectedUnit.occupancyPeriods.length > 0
        ) {
          allCierres = allCierres.filter((c) =>
            occupancyCoversPeriod(selectedUnit.occupancyPeriods, c.month, c.year)
          );
        }

        // Ordenar descendente por año y mes
        allCierres.sort((a, b) => {
          if (b.year !== a.year) return b.year - a.year;
          return b.month - a.month;
        });

        setCierres(allCierres);

        // Seleccionar el mas reciente por defecto
        if (allCierres.length > 0) {
          setSelectedCierreId(allCierres[0].id);
        } else {
          setSelectedCierreId(null);
          setExpenses([]);
        }

        // Fetch mapa de proveedores. Best-effort: la demo no lee el catálogo
        // global (reglas 0D) y eso no tiene que vaciar la lista de cierres; las
        // facturas se muestran con su descripción.
        const sMap = {};
        try {
          const suppliersSnap = await getDocs(collection(db, "supplier"));
          suppliersSnap.docs.forEach((d) => {
            const data = d.data();
            sMap[data.supplierId] = data.name;
          });
        } catch (supErr) {
          console.warn("No se pudieron cargar los proveedores:", supErr?.code || supErr?.message);
        }
        setSuppliersMap(sMap);
      } catch (error) {
        console.error("Error al cargar cierres:", error);
        setCierres([]);
        setSelectedCierreId(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCierresAndSuppliers();
  }, [selectedBuilding, selectedUnit]);

  // Fetch gastos del cierre seleccionado
  const selectedBuildingId = selectedBuilding?.buildingid;
  useEffect(() => {
    const fetchExpensesForCierre = async () => {
      if (!selectedCierreId || selectedBuildingId == null) {
        setExpenses([]);
        return;
      }
      setLoadingExpenses(true);

      try {
        // Obtener expensesapartments del cierre, acotados al edificio (reglas
        // 0D): así la consulta pasa también con los ids de cierre viejos
        // (aleatorios, anteriores a marzo 2026).
        const eaSnap = await getDocs(
          consultaDelCierre(db, "expensesapartments", selectedCierreId, selectedBuildingId)
        );
        const publishedEAs = eaSnap.docs
          .map((d) => d.data())
          .filter(isExpenseApartmentPublishedToResidents);

        // Extraer expenseId unicos
        const expenseIds = [...new Set(publishedEAs.map((ea) => ea.expenseId).filter(Boolean))];

        if (expenseIds.length === 0) {
          setExpenses([]);
          setLoadingExpenses(false);
          return;
        }

        // Gastos por id con getDoc: una consulta por documentId no fija el
        // edificio y las reglas 0D la niegan entera.
        const allExpenses = await leerGastosPorId(db, expenseIds);

        // Ordenar por fecha de factura descendente
        allExpenses.sort((a, b) => {
          const dateA = parseInvoiceDate(a.invoiceDate);
          const dateB = parseInvoiceDate(b.invoiceDate);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        });

        setExpenses(allExpenses);
      } catch (error) {
        console.error("Error al cargar gastos del cierre:", error);
        setExpenses([]);
      } finally {
        setLoadingExpenses(false);
      }
    };

    fetchExpensesForCierre();
  }, [selectedCierreId, selectedBuildingId]);

  // Label del cierre seleccionado
  const selectedCierre = cierres.find((c) => c.id === selectedCierreId);

  // ─── Versión mobile: estilo app "Comprobantes" (screen 10) ──────────────────
  if (isMobile) {
    const idx = cierres.findIndex((c) => c.id === selectedCierreId);
    const cierreLabel = selectedCierre
      ? `${monthNames[selectedCierre.month - 1]} ${selectedCierre.year}`
      : "—";
    // Derivados de presentación (read-only): total del mes y cantidad de facturas.
    const totalMes = expenses.reduce((acc, e) => acc + (Number(e.amount) || 0), 0);
    const facturasConLink = expenses.filter((e) => e.bill).length;
    return (
      <AppScreen
        header={
          <AppHeader
            title={t("title")}
            subtitle={t("subtitleMobile")}
            onBack={goBack}
          />
        }
      >
        {loading ? (
          <p style={{ ...appType.body, color: appColors.ink2 }}>{t("loadingClosings")}</p>
        ) : cierres.length === 0 ? (
          <AppEmptyState
            icon={<FaCalendarAlt />}
            title={t("noClosings.title")}
            description={t("noClosings.description")}
          />
        ) : (
          <>
            {/* Selector de cierre ‹ Mes Año › */}
            <SectionLabel style={{ marginBottom: 10 }}>{t("period")}</SectionLabel>
            <AppCard variant="cream" padding={12} style={{ marginBottom: appSpacing.section }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <button
                  type="button"
                  aria-label={t("prevClosing")}
                  disabled={idx >= cierres.length - 1}
                  onClick={() => idx < cierres.length - 1 && setSelectedCierreId(cierres[idx + 1].id)}
                  style={stepBtn(idx >= cierres.length - 1)}
                >
                  ‹
                </button>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 0 }}>
                  <span style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("closingMonth")}</span>
                  <span style={{ ...appType.cardTitle, fontSize: 16 }}>{cierreLabel}</span>
                </div>
                <button
                  type="button"
                  aria-label={t("nextClosing")}
                  disabled={idx <= 0}
                  onClick={() => idx > 0 && setSelectedCierreId(cierres[idx - 1].id)}
                  style={stepBtn(idx <= 0)}
                >
                  ›
                </button>
              </div>
            </AppCard>

            {loadingExpenses ? (
              <p style={{ ...appType.body, color: appColors.ink2 }}>{t("loadingExpenses")}</p>
            ) : expenses.length === 0 ? (
              <AppEmptyState
                icon={<FaFileInvoice />}
                title={t("noInvoices.title")}
                description={t("noInvoices.description", { label: cierreLabel })}
              />
            ) : (
              <>
                {/* Resumen del mes: total en caja fría resaltada */}
                <AppCard variant="cream" padding={16} style={{ marginBottom: appSpacing.section }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("totalMonth")}</div>
                      <div
                        style={{
                          ...appType.amount,
                          fontSize: 26,
                          marginTop: 4,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {formatCurrencyUY(totalMes)}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", minWidth: 0 }}>
                      <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("invoicesStat")}</div>
                      <div style={{ ...appType.stat, fontSize: 22, marginTop: 4, color: appColors.blue }}>
                        {expenses.length}
                      </div>
                    </div>
                  </div>
                </AppCard>

                <SectionLabel style={{ marginBottom: 10 }}>
                  {t("receiptsHeading", { n: expenses.length })}
                </SectionLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {expenses.map((e) => {
                    const d = parseInvoiceDate(e.invoiceDate);
                    const fecha = d && !isNaN(d.getTime()) ? formatDateLocalized(d) : null;
                    return (
                      <AppCard key={e.id} padding={16}>
                        {/* Encabezado: proveedor + monto */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <IconBox icon={<FaFileAlt />} size={40} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 3 }}>
                              {t("supplier")}
                            </div>
                            <div style={{ ...appType.cardTitle, fontSize: 15, overflowWrap: "anywhere" }}>
                              {suppliersMap[e.supplierid] || e.description || t("invoiceFallback")}
                            </div>
                          </div>
                        </div>

                        {/* Footer estructurado: FECHA / Nº / IMPORTE */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr auto",
                            gap: 10,
                            alignItems: "end",
                            marginTop: 14,
                            paddingTop: 12,
                            borderTop: `1px solid ${appColors.line2}`,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("date")}</div>
                            <div style={{ ...appType.body, fontSize: 13, marginTop: 2 }}>
                              {fecha || "—"}
                            </div>
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("invoiceNumber")}</div>
                            <div style={{ ...appType.body, fontSize: 13, marginTop: 2, overflowWrap: "anywhere" }}>
                              {e.receiptNumber || "—"}
                            </div>
                          </div>
                          <div style={{ textAlign: "right", minWidth: 0 }}>
                            <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("amount")}</div>
                            <div
                              style={{
                                ...appType.body,
                                fontSize: 16,
                                marginTop: 2,
                                fontVariantNumeric: "tabular-nums",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {formatCurrencyUY(e.amount)}
                            </div>
                          </div>
                        </div>

                        {/* Link a la factura, ancho completo */}
                        {e.bill && (
                          <a
                            href={e.bill}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              marginTop: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 8,
                              padding: "10px 12px",
                              borderRadius: 14,
                              background: appColors.surface2,
                              color: appColors.blue,
                              textDecoration: "none",
                              fontFamily: appType.fontFamily,
                              fontSize: 13,
                              fontWeight: 400,
                            }}
                          >
                            <FaDownload style={{ fontSize: 12 }} /> {t("viewInvoiceMobile")}
                          </a>
                        )}
                      </AppCard>
                    );
                  })}
                </div>

                {/* Nota de pie: cuántas tienen comprobante adjunto */}
                {facturasConLink < expenses.length && (
                  <div
                    style={{
                      ...appType.caption,
                      textAlign: "center",
                      marginTop: 14,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {t("attachedNote", { withLink: facturasConLink, total: expenses.length })}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </AppScreen>
    );
  }

  // Columnas para DataTable
  const columns = [
    {
      key: "supplierid",
      label: t("columns.supplier"),
      render: (val) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: colors.black }}>
          <FaFileAlt style={{ color: colors.primary, fontSize: 16 }} />
          {suppliersMap[val] || "—"}
        </div>
      ),
    },
    {
      key: "description",
      label: t("columns.description"),
    },
    {
      key: "receiptNumber",
      label: t("columns.receiptNumber"),
      render: (val) =>
        val ? (
          <span style={{ ...badgeBase, backgroundColor: colors.primaryLighter, color: colors.primary }}>
            {val}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "invoiceDate",
      label: t("columns.date"),
      render: (val) => {
        const d = parseInvoiceDate(val);
        if (!d || isNaN(d.getTime())) return "—";
        return formatDateLocalized(d);
      },
    },
    {
      key: "amount",
      label: t("columns.amount"),
      align: "right",
      render: (val) => (
        <span style={{ color: colors.black }}>{formatCurrencyUY(val)}</span>
      ),
    },
    {
      key: "bill",
      label: t("columns.invoice"),
      render: (val) =>
        val ? (
          <a
            href={val}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.downloadLink}
          >
            <FaDownload style={{ fontSize: 12 }} />
            {t("viewInvoice")}
          </a>
        ) : (
          <span style={{ color: colors.gray400 }}>—</span>
        ),
    },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerIcon}>
          <FaFileInvoice />
        </div>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>{t("title")}</h1>
          <p style={styles.subtitle}>{t("subtitle")}</p>
        </div>
      </div>

      {loading ? (
        <div style={styles.loadingContainer}>
          <p style={styles.loadingText}>{t("loadingClosings")}</p>
        </div>
      ) : cierres.length === 0 ? (
        <div style={cardBase}>
          <EmptyState
            icon={FaCalendarAlt}
            title={t("noClosings.title")}
            description={t("noClosings.description")}
          />
        </div>
      ) : (
        <>
          {/* Selector de cierre */}
          <div style={styles.selectorRow}>
            <div style={styles.selectorWrapper}>
              <label htmlFor="cierre-select" style={styles.selectorLabel}>
                {t("closingLabel")}
              </label>
              <select
                id="cierre-select"
                value={selectedCierreId || ""}
                onChange={(e) => setSelectedCierreId(e.target.value)}
                style={styles.select}
                className="habitia-input"
              >
                {cierres.map((c) => (
                  <option key={c.id} value={c.id}>
                    {monthNames[c.month - 1]} {c.year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tabla de gastos */}
          {loadingExpenses ? (
            <div style={styles.loadingContainer}>
              <p style={styles.loadingText}>{t("loadingExpenses")}</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={expenses}
              emptyIcon={FaFileInvoice}
              emptyTitle={t("noExpenses.title")}
              emptyDescription={
                selectedCierre
                  ? t("noExpenses.withClosing", {
                      label: `${monthNames[selectedCierre.month - 1]} ${selectedCierre.year}`,
                    })
                  : t("noExpenses.noSelection")
              }
            />
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: spacing[6],
    backgroundColor: colors.bgMain,
    minHeight: "100vh",
    fontFamily: typography.fontFamily,
  },
  header: {
    background: appColors.surface,
    borderRadius: 10,
    padding: "16px 20px",
    boxShadow: appShadows.soft,
    border: `1px solid ${appColors.line}`,
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: spacing[5],
  },
  headerIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: appColors.blue,
    color: appColors.onBlue,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: 400,
    margin: 0,
    color: appColors.blue,
  },
  subtitle: {
    fontSize: 13,
    color: appColors.ink2,
    margin: "2px 0 0 0",
    fontWeight: 400,
  },
  selectorRow: {
    display: "flex",
    justifyContent: "center",
    marginBottom: spacing[5],
  },
  selectorWrapper: {
    ...cardBase,
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    padding: `${spacing[3]}px ${spacing[5]}px`,
  },
  selectorLabel: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    fontWeight: 400,
    whiteSpace: "nowrap",
  },
  select: {
    ...inputBase,
    width: "auto",
    minWidth: 200,
    cursor: "pointer",
  },
  downloadLink: {
    backgroundColor: colors.primary,
    color: colors.white,
    padding: "8px 14px",
    borderRadius: 8,
    textDecoration: "none",
    fontWeight: 400,
    fontSize: 13,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "background-color 0.2s ease",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: `${spacing[12]}px ${spacing[5]}px`,
  },
  loadingText: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    fontStyle: "italic",
  },
};

export default Documentos;
